// EOL/EOS Page - Main container component

import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Text,
  Pivot,
  PivotItem,
  PrimaryButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  useTheme,
  mergeStyleSets,
} from '@fluentui/react';
import { EolEosProvider, useEolEos } from './contexts';
import { ProductsTable } from './components/ProductsTable';
import { Pagination } from './components/Pagination';
import { SearchForm } from './components/SearchForm';
import { ProductFormModal } from './components/ProductFormModal';
import { CsvUploadPanel } from './components/CsvUploadPanel';
import { ProductRelationsModal } from './components/ProductRelationsModal';
import type { EndOfLifeProduct, SearchCriteria, PaginationInfo } from './types';

const EolEosPageContent: React.FC = () => {
  const theme = useTheme();
  const { getProducts, searchProducts, deleteProduct, canRead, canWrite, isLoading, error } = useEolEos();

  const [products, setProducts] = useState<EndOfLifeProduct[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalCount: 0,
    currentPage: 1,
    itemsPerPage: 30,
    totalPages: 0,
  });
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria | null>(null);
  const [selectedPivot, setSelectedPivot] = useState<string>('products');
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState<EndOfLifeProduct | null>(null);
  const [isRelationsModalOpen, setIsRelationsModalOpen] = useState(false);
  const [productForRelations, setProductForRelations] = useState<EndOfLifeProduct | null>(null);

  const styles = mergeStyleSets({
    container: {
      padding: '24px',
      backgroundColor: theme.palette.neutralLighterAlt,
      minHeight: '100vh',
    },
    header: {
      marginBottom: '24px',
    },
    title: {
      fontSize: '28px',
      fontWeight: 600,
      color: theme.palette.neutralPrimary,
    },
    content: {
      backgroundColor: theme.palette.white,
      borderRadius: '4px',
      boxShadow: theme.effects.elevation4,
    },
    toolbar: {
      padding: '16px 24px',
      borderBottom: `1px solid ${theme.palette.neutralLight}`,
    },
  });

  // Load products
  const loadProducts = useCallback(
    async (page: number = 1, pageSize: number = 30, criteria: SearchCriteria | null = null) => {
      let result;
      if (criteria) {
        result = await searchProducts(criteria, page, pageSize);
      } else {
        result = await getProducts(page, pageSize);
      }

      if (result) {
        setProducts(result.data);
        setPagination(result.pagination);
      }
    },
    [getProducts, searchProducts]
  );

  // Initial load
  useEffect(() => {
    if (canRead) {
      void loadProducts(pagination.currentPage, pagination.itemsPerPage, searchCriteria);
    }
  }, [canRead]); // Only run on mount when authorized

  // Handle page change
  const handlePageChange = (newPage: number) => {
    void loadProducts(newPage, pagination.itemsPerPage, searchCriteria);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    void loadProducts(1, newPageSize, searchCriteria);
  };

  // Handle search
  const handleSearch = (criteria: SearchCriteria) => {
    setSearchCriteria(criteria);
    void loadProducts(1, pagination.itemsPerPage, criteria);
    setSelectedPivot('products');
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchCriteria(null);
    void loadProducts(1, pagination.itemsPerPage, null);
  };

  // Handle add product
  const handleAddProduct = () => {
    setProductToEdit(null);
    setIsProductFormOpen(true);
  };

  // Handle edit product
  const handleEditProduct = (product: EndOfLifeProduct) => {
    setProductToEdit(product);
    setIsProductFormOpen(true);
  };

  // Handle delete product
  const handleDeleteProduct = async (productId: number) => {
    const success = await deleteProduct(productId);
    if (success) {
      // Reload current page
      void loadProducts(pagination.currentPage, pagination.itemsPerPage, searchCriteria);
    }
  };

  // Handle manage relations
  const handleManageRelations = (product: EndOfLifeProduct) => {
    setProductForRelations(product);
    setIsRelationsModalOpen(true);
  };

  // Handle product save
  const handleProductSave = () => {
    void loadProducts(pagination.currentPage, pagination.itemsPerPage, searchCriteria);
  };

  if (!canRead) {
    return (
      <Stack className={styles.container}>
        <MessageBar messageBarType={MessageBarType.warning}>
          You do not have permission to view EOL/EOS products.
        </MessageBar>
      </Stack>
    );
  }

  return (
    <Stack className={styles.container}>
      <Stack.Item className={styles.header}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Text className={styles.title}>End of Life / End of Support Tracking</Text>
          {canWrite && (
            <PrimaryButton
              text="Add Product"
              iconProps={{ iconName: 'Add' }}
              onClick={handleAddProduct}
            />
          )}
        </Stack>
      </Stack.Item>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => {}}>
          {error}
        </MessageBar>
      )}

      <Stack.Item className={styles.content}>
        <Pivot selectedKey={selectedPivot} onLinkClick={(item) => setSelectedPivot(item?.props.itemKey || 'products')}>
          <PivotItem headerText="Products" itemKey="products">
            <Stack>
              {isLoading ? (
                <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { padding: '48px' } }}>
                  <Spinner size={SpinnerSize.large} label="Loading products..." />
                </Stack>
              ) : (
                <>
                  <ProductsTable
                    products={products}
                    onEditProduct={handleEditProduct}
                    onDeleteProduct={handleDeleteProduct}
                    onManageRelations={handleManageRelations}
                    canWrite={canWrite}
                  />
                  {products.length > 0 && (
                    <Pagination
                      pagination={pagination}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  )}
                  {products.length === 0 && !isLoading && (
                    <Stack horizontalAlign="center" styles={{ root: { padding: '48px' } }}>
                      <Text>No products found.</Text>
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </PivotItem>

          <PivotItem headerText="Search" itemKey="search">
            <SearchForm onSearch={handleSearch} onClear={handleClearSearch} />
          </PivotItem>

          <PivotItem headerText="Upload CSV" itemKey="upload">
            <Stack styles={{ root: { padding: '24px' } }}>
              <CsvUploadPanel />
            </Stack>
          </PivotItem>
        </Pivot>
      </Stack.Item>

      <ProductFormModal
        isOpen={isProductFormOpen}
        product={productToEdit}
        onDismiss={() => setIsProductFormOpen(false)}
        onSave={handleProductSave}
      />

      <ProductRelationsModal
        isOpen={isRelationsModalOpen}
        product={productForRelations}
        onDismiss={() => setIsRelationsModalOpen(false)}
      />
    </Stack>
  );
};

// Wrapper component with provider
export const EolEosPage: React.FC = () => {
  return (
    <EolEosProvider>
      <EolEosPageContent />
    </EolEosProvider>
  );
};
